import { Location } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { OrderService } from "../../services/order/order.service";
import { OrderItemAttributes } from "@models/orderItem.model";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import {
    CancelReasonPayload,
    DialogSaveDataModel,
    ExchangeReasonPayload,
    RefundOption,
    ReturnReasonPayload,
} from "@models/order.model";
import { accountHolderNameValidator } from "../../shared/account-holder-name.validator";
import { indianBankAccountValidator } from "../../shared/account-number-validator.directive";
import { ifscValidator } from "../../shared/ifsc-validator.directive";
import { upiValidator } from "../../shared/upiId-validator.directive";
import { PaymentMethod } from "@models/payment.model";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";

@Component({
    selector: "app-return-replace-page",
    templateUrl: "./return-replace-page.component.html",
    styleUrl: "./return-replace-page.component.scss",
    standalone: false,
})
export class ReturnReplacePageComponent implements OnInit {
    private orderId!: string;
    private orderItemIds: Set<number> = new Set<number>();
    public action!: string;
    public heading!: string;
    public orderItems!: OrderItemAttributes[];
    public label!: string;
    public returnExchangeForm!: FormGroup;
    public isCod: boolean = false;
    public isLoading: boolean = false;
    public totalAmount!: number;

    constructor(
        private location: Location,
        private router: Router,
        private route: ActivatedRoute,
        private orderService: OrderService,
        private fromBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            (this.orderId = params["id"]), (this.action = params["action"]);
            const labelKey =
                this.action === "return"
                    ? "returnReplaceCancel.questioning.return"
                    : this.action === "exchange"
                    ? "returnReplaceCancel.questioning.exchange"
                    : "returnReplaceCancel.questioning.cancel";

            this.getTranslationAsync(labelKey).subscribe((translated: string) => {
                this.label = translated;
            });

            const headingKey =
                this.action === "return"
                    ? "returnReplaceCancel.heading.return"
                    : this.action === "exchange"
                    ? "returnReplaceCancel.heading.exchange"
                    : "returnReplaceCancel.heading.cancel";

            this.getTranslationAsync(headingKey).subscribe((translated: string) => {
                this.heading = translated;
            });
        });
        this.orderItemIds = history.state.selectedItemIds;
        if (this.orderId) {
            this.loadOrderDetails();
        }

        this.returnExchangeForm = this.fromBuilder.group({
            refundOption: ["bankDetails"],
            bankDetails: this.fromBuilder.group({
                accountHolderName: [""],
                accountNumber: [""],
                ifscCode: [""],
            }),
            upi: this.fromBuilder.group({
                upiId: [""],
            }),
            reasons: this.fromBuilder.array([]), // One entry per product
        });

        this.returnExchangeForm.get("refundOption")?.valueChanges.subscribe((value) => {
            if (this.action === "return" && this.isCod) {
                if (value === "bankDetails") {
                    this.setBankDetailsValidators();
                    this.clearUpiValidators();
                    this.returnExchangeForm.get("upi")?.reset();
                } else if (value === "upi") {
                    this.setUpiValidators();
                    this.clearBankDetailsValidators();
                    this.returnExchangeForm.get("bankDetails")?.reset();
                }
            }
            this.cdr.detectChanges();
        });

        this.applyConditionalValidators();
    }

    public setBankDetailsValidators(): void {
        const bankDetails = this.returnExchangeForm.get("bankDetails");
        bankDetails?.get("accountHolderName")?.setValidators([Validators.required, accountHolderNameValidator()]);
        bankDetails?.get("accountNumber")?.setValidators([Validators.required, indianBankAccountValidator()]);
        bankDetails?.get("ifscCode")?.setValidators([Validators.required, ifscValidator()]);
        bankDetails?.updateValueAndValidity();
    }

    public clearBankDetailsValidators(): void {
        const bankDetails = this.returnExchangeForm.get("bankDetails");
        bankDetails?.get("accountHolderName")?.clearValidators();
        bankDetails?.get("accountNumber")?.clearValidators();
        bankDetails?.get("ifscCode")?.clearValidators();
        bankDetails?.updateValueAndValidity();
    }

    public setUpiValidators(): void {
        const upi = this.returnExchangeForm.get("upi");
        upi?.get("upiId")?.setValidators([Validators.required, upiValidator()]);
        upi?.updateValueAndValidity();
    }

    public clearUpiValidators(): void {
        const upi = this.returnExchangeForm.get("upi");
        upi?.get("upiId")?.clearValidators();
        upi?.updateValueAndValidity();
    }

    applyConditionalValidators(): void {
        const reason = this.returnExchangeForm.get("reason");
        const refundOption = this.returnExchangeForm.get("refundOption");

        // Clear all first
        refundOption?.clearValidators();
        this.clearBankDetailsValidators();
        this.clearUpiValidators();

        if (this.action === "return") {
            if (this.isCod) {
                refundOption?.setValidators([Validators.required]);

                // Re-run logic for chosen refund option (bank or upi)
                const optionValue = refundOption?.value;
                if (optionValue === "bankDetails") {
                    this.setBankDetailsValidators();
                } else if (optionValue === "upi") {
                    this.setUpiValidators();
                }
            } else {
                // If COD, refund option is not needed
                this.returnExchangeForm.get("bankDetails")?.reset();
                this.returnExchangeForm.get("upi")?.reset();
            }
        } else if (this.action === "exchange") {
            // Reset refund-related fields
            this.returnExchangeForm.get("refundOption")?.reset();
            this.returnExchangeForm.get("bankDetails")?.reset();
            this.returnExchangeForm.get("upi")?.reset();
        }

        refundOption?.updateValueAndValidity();
        this.returnExchangeForm.get("bankDetails")?.updateValueAndValidity();
        this.returnExchangeForm.get("upi")?.updateValueAndValidity();
    }

    private loadOrderDetails(): void {
        this.isLoading = true;
        this.orderService.getOrderById(this.orderId).subscribe((response) => {
            this.isCod = response.paymentMethod === PaymentMethod.CashOnDelivery;
            if (this.action === "return" && this.isCod) {
                const currentRefundOption = this.returnExchangeForm.get("refundOption")?.value;
                if (currentRefundOption === "bankDetails") {
                    this.setBankDetailsValidators();
                } else if (currentRefundOption === "upi") {
                    this.setUpiValidators();
                }
            }

            this.orderItems = response.orderItems?.filter((item) => this.orderItemIds.has(item.id!))!;

            const reasonsArray = this.returnExchangeForm.get("reasons") as FormArray;
            this.orderItems.forEach(() => {
                reasonsArray.push(
                    this.fromBuilder.group({
                        reason: ["", Validators.required],
                    }),
                );
            });
            this.isLoading = false;
            this.cdr.detectChanges();
            this.calculateTotalAmountSelectedItems();
        });
    }

    public goBack(): void {
        this.location.back();
    }

    public submit(): void {
        if (this.action === "return") {
            this.confirmReturnSelectedItem();
        } else if (this.action === "cancel") {
            this.confirmCancelSelectedItem();
        } else {
            this.confirmExchangeSelectedItem();
        }
    }

    private confirmReturnSelectedItem(): void {
        const data = this.returnExchangeForm.value;
        const reasonsArray = data.reasons;

        const result: ReturnReasonPayload[] = this.orderItems.map((item, index) => {
            return {
                id: item.id,
                returnReason: reasonsArray[index].reason,
            };
        });

        const refundData: any = {
            refundOption: data.refundOption,
        };

        if (data.refundOption === RefundOption.BankDetails) {
            if (data.bankDetails?.accountHolderName) {
                refundData.accountHolderName = data.bankDetails.accountHolderName;
            }
            if (data.bankDetails?.accountNumber) {
                refundData.accountNumber = data.bankDetails.accountNumber;
            }
            if (data.bankDetails?.ifscCode) {
                refundData.ifscCode = data.bankDetails.ifscCode;
            }
        }

        if (data.refundOption === RefundOption.Upi && data.upi?.upiId) {
            refundData.upiId = data.upi.upiId;
        }
        this.orderService
            .returnSelectedItem(
                this.orderId,
                result,
                refundData.refundOption,
                refundData.accountHolderName,
                refundData.accountNumber,
                refundData.ifscCode,
                refundData.upiId,
            )
            .subscribe((response) => {
                if (response) {
                    this.router.navigate([`account/orders/${this.orderId}/${this.action}/reason/success`], {
                        state: { seletedItems: this.orderItems, total: this.totalAmount },
                    });
                }
            });
    }

    public confirmExchangeSelectedItem(): void {
        const data = this.returnExchangeForm.value;
        const reasonsArray = data.reasons;

        const result: ExchangeReasonPayload[] = this.orderItems.map((item, index) => {
            return {
                id: item.id,
                exchangeReason: reasonsArray[index].reason,
            };
        });

        this.orderService.exchangeSelectedItem(this.orderId, result).subscribe({
            next: (response) => {
                if (response) {
                    this.router.navigate([`account/orders/${this.orderId}/${this.action}/reason/success`], {
                        state: { seletedItems: this.orderItems, total: this.totalAmount },
                    });
                }
            },
            error: (error) => {
                throw error;
            },
        });
    }

    getReasonControl(index: number): FormControl {
        return ((this.returnExchangeForm.get("reasons") as FormArray).at(index) as FormGroup).get(
            "reason",
        ) as FormControl;
    }

    private confirmCancelSelectedItem(): void {
        const data = this.returnExchangeForm.value;
        const reasonsArray = data.reasons;

        const result: CancelReasonPayload[] = this.orderItems.map((item, index) => {
            return {
                id: item.id,
                cancelReason: reasonsArray[index].reason,
            };
        });

        this.orderService.cancelSelectedItem(this.orderId, result).subscribe({
            next: (response) => {
                if (response) {
                    this.router.navigate([`account/orders/${this.orderId}/${this.action}/reason/success`], {
                        state: { seletedItems: this.orderItems, total: this.totalAmount },
                    });
                }
            },
            error: (error) => {
                throw error;
            },
        });
    }

    public getTranslationAsync(key: string, params?: object): Observable<string> {
        return this.translate.get(key, params);
    }

    public calculateTotalAmountSelectedItems(): void {
        this.totalAmount = this.orderItems.reduce((total, item) => {
            const price = item.discountedPrice ?? item.price ?? 0;
            return total + price;
        }, 0);
    }
}
